import { MAX_CATCHUP_TICKS, MAX_FRAME_MS, TICK_MS } from '../sim/constants';
import { step } from '../sim/step';
import { compareCommands } from '../sim/commands';
import type { Command } from '../sim/commands';
import type { Grid } from '../sim/path';
import type { GameStore } from './GameStore';
import type { CommandSource } from './CommandSource';
import type { SimEvent } from '../sim/types';

/** מוחזר כשאין אירועים — כדי לא להקצות מערך חדש בכל פריים. */
const EMPTY_EVENTS: readonly SimEvent[] = [];

/*
 * לולאת המשחק.
 *
 * **טיק קבוע של 50ms, בלי dt.** מהירות המשחק אינה מכפילה את גודל הצעד אלא את מספר
 * הטיקים — כך ש-4x הוא רצף של אותם טיקים בדיוק, וזהה ביט-לביט לריצה ב-1x. אילו
 * הטיק היה תלוי בזמן שעבר, כל מחשב היה מקבל משחק אחר וכל השרשרת (טסטים, שמירה,
 * replay, מולטיפלייר) הייתה נופלת.
 *
 * `advance()` ציבורית ונפרדת מ-`start()` בכוונה: כך טסטים מריצים זמן מדומה בלי
 * requestAnimationFrame, ובלי לחכות בזמן אמת.
 *
 * **`ready()` של מקורות הפקודות נבדק לפני כל טיק.** מקומי ו-AI תמיד מוכנים, ולכן
 * במשחק יחיד זה קוד מת — אבל זה בדיוק המקום שבו lockstep חי: מקור רשת מחזיר false
 * עד שהגיעו הפקודות של כולם לאותו טיק, והלולאה קופאת במקום להריץ טיק שהיריב יריץ
 * אחרת. אלטרנטיבה של "להריץ ולתקן אחר כך" מחייבת גלגול מצב לאחור, וזה משחק אחר.
 */
export class GameLoop {
    private accumulator = 0;
    private alphaValue = 0;
    private paused = false;
    private speedValue = 1;
    private handle = 0;
    private lastTime = 0;
    private stalledValue = false;
    private onFrame: ((alpha: number) => void) | null = null;
    /** אירועים שנצברו מטיקים שרצו מאז ה-`drainEvents` האחרון. */
    private pending: SimEvent[] = [];

    constructor(
        private readonly store: GameStore,
        private readonly grid: Grid,
        private readonly sources: readonly CommandSource[],
    ) {}

    /** יחס ההשלמה אל הטיק הבא, לשזירת התנועה בציור. 0 כשמושהה. */
    get alpha(): number {
        return this.alphaValue;
    }

    get isPaused(): boolean {
        return this.paused;
    }

    /**
     * הלולאה מוכנה לרוץ אבל אחד ממקורות הפקודות עדיין לא. במשחק רשת זה "ממתין
     * ליריב", וזה מצב תקין שצריך להיראות בממשק — לא באג.
     */
    get stalled(): boolean {
        return this.stalledValue;
    }

    get speed(): number {
        return this.speedValue;
    }

    setPaused(value: boolean): void {
        this.paused = value;
        if (value) this.alphaValue = 0;
    }

    setSpeed(value: number): void {
        this.speedValue = Math.max(0.25, Math.min(8, value));
    }

    /**
     * מקדם את הסימולציה לפי הזמן שעבר. מחזיר כמה טיקים רצו בפועל.
     * הזמן נחתך ב-MAX_FRAME_MS ומספר הטיקים ב-MAX_CATCHUP_TICKS, אחרת חזרה מטאב
     * שהיה ברקע חמש דקות מנסה להריץ 6,000 טיקים בפריים אחד ומקפיאה את הדפדפן.
     */
    advance(deltaMs: number): number {
        if (this.paused) {
            this.alphaValue = 0;
            return 0;
        }

        this.accumulator += Math.min(deltaMs, MAX_FRAME_MS) * this.speedValue;

        let ran = 0;
        let blocked = false;
        while (this.accumulator >= TICK_MS && ran < MAX_CATCHUP_TICKS) {
            if (!this.canRun(this.store.cur.tick)) {
                blocked = true;
                break;
            }
            this.runTick();
            this.accumulator -= TICK_MS;
            ran++;
        }
        this.stalledValue = blocked;

        if (blocked) {
            /*
             * חוב הזמן נחסם גם בהמתנה לרשת. בלי זה, המתנה של עשר שניות הייתה
             * נפרעת אחריה בריצה מטורפת של מאות טיקים — כלומר בדיוק מפולת הטיקים
             * שהתקרה למעלה נועדה למנוע, רק בדלת אחרת.
             */
            this.accumulator = Math.min(this.accumulator, TICK_MS * MAX_CATCHUP_TICKS);
        } else if (ran === MAX_CATCHUP_TICKS) {
            // ויתור על החוב שנותר, במקום לגרור אותו לפריימים הבאים ולגמגם.
            this.accumulator = Math.min(this.accumulator, TICK_MS);
        }

        this.alphaValue = Math.min(1, this.accumulator / TICK_MS);
        if (ran > 0) this.store.notify();
        return ran;
    }

    /** הטיק רץ רק כשכל מקורות הפקודות יודעים מה יש להם לומר עליו. */
    private canRun(tick: number): boolean {
        for (const source of this.sources) {
            if (!source.ready(tick)) return false;
        }
        return true;
    }

    private runTick(): void {
        const tick = this.store.cur.tick;
        const commands: Command[] = [];
        for (const source of this.sources) commands.push(...source.drain(tick));
        commands.sort(compareCommands);
        this.store.commit(step(this.store.cur, commands, this.grid));
        // האירועים נצברים כאן ולא נקראים מ-`store.cur` בסוף הפריים: `cur` מחזיק
        // רק את אירועי הטיק **האחרון**. ראו `drainEvents`.
        for (const event of this.store.cur.events) this.pending.push(event);
    }

    /**
     * שולף את האירועים שנצברו מאז הקריאה הקודמת, ומרוקן.
     *
     * **זה לא נוחות אלא תיקון לבאג כפול.** הרנדרר קרא `store.cur.events` בכל
     * פריים, ומזה נבעו שתי תקלות מנוגדות: ב-60fps ובמהירות 1x טיק רץ בערך כל
     * פריים שלישי, ולכן **אותם** אירועים נצרכו שלוש פעמים — פיצוץ אחד הוליד פי
     * שלושה חלקיקים ושרף את תקרת החלקיקים; ובמהירות 4x, כשכמה טיקים רצים בפריים
     * אחד, `cur.events` הכיל רק את האחרון וכל השאר **נעלמו**. מונה טיקים היה
     * פותר רק את הראשונה.
     */
    drainEvents(): readonly SimEvent[] {
        if (this.pending.length === 0) return EMPTY_EVENTS;
        const events = this.pending;
        this.pending = [];
        return events;
    }

    start(onFrame: (alpha: number) => void): void {
        this.onFrame = onFrame;
        this.lastTime = performance.now();
        const frame = (now: number) => {
            const delta = now - this.lastTime;
            this.lastTime = now;
            this.advance(delta);
            this.onFrame?.(this.alphaValue);
            this.handle = requestAnimationFrame(frame);
        };
        this.handle = requestAnimationFrame(frame);
    }

    stop(): void {
        if (this.handle) cancelAnimationFrame(this.handle);
        this.handle = 0;
        this.onFrame = null;
    }

    /** מאפס את שעון הלולאה — אחרי חזרה מרקע, כדי לא לצבור חוב זמן ענק. */
    resetClock(): void {
        this.lastTime = performance.now();
        this.accumulator = 0;
    }
}
